/**
 * @file MlrRecordsReviewPanel.tsx
 * @brief §6 — which records the doctor reviewed, said by them and confirmed.
 *
 * @details
 * ⚠️ **This replaces a sentence the report used to invent.**
 *
 * Whenever Case Hub said records had arrived, §6 printed five named record
 * types — GP, ambulance, A&E, hospital, radiology — and asserted they were
 * "legible and complete". One boolean meaning "something arrived" produced six
 * unchecked claims in signed expert evidence. On a case where the instructing
 * party sent a GP summary and nothing else, that paragraph was false in five
 * places.
 *
 * The mobile card is where a doctor DICTATES this ("I've got the GP notes and
 * the A&E card"); this is the desk surface where they tick and correct. Same
 * answer, same endpoint, and the same rule at the end of it: §6 prints nothing
 * from this until `confirmed`.
 *
 * Silence is a real answer here and has no control of its own. Leaving
 * legibility unset means the report says nothing about it — which is precisely
 * what the old paragraph could not do.
 *
 * @references BACKEND/src/features/medical-assessment/recordsReview.schema.mjs
 * @author DES
 * @date 17-09-2026
 */

import { useMemo, useState } from "react";
import { Check, Quote } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { toast } from "@/shared/hooks/useToast";
import { useSaveRecordsReview } from "../api";
import type {
  MlrRecordSourceKey,
  MlrRecordsReview,
  MlrRecordsStatus,
} from "../schemas";

/**
 * Short screen labels, mirrored from `recordsReview.schema.mjs`.
 *
 * NOT a second copy of the report's wording — the printed sentence is built
 * server-side and never from here. These label a control the doctor clicks and
 * are allowed to be shorter than the prose they lead to.
 */
const SOURCE_LABEL: Record<MlrRecordSourceKey, string> = {
  gp: "GP records",
  ambulance: "Ambulance",
  aande: "A&E",
  hospital: "Hospital",
  radiology: "Radiology",
  physiotherapy: "Physiotherapy",
  occupationalHealth: "Occupational health",
  priorMedicolegal: "Previous reports",
  instructions: "Instructions",
  compensator: "Compensator info",
};

const SOURCE_ORDER = Object.keys(SOURCE_LABEL) as MlrRecordSourceKey[];

const STATUS_LABEL: Record<MlrRecordsStatus, string> = {
  reviewed: "Reviewed",
  none_received: "None received",
  awaited: "Awaited",
};

const EMPTY: MlrRecordsReview = {
  status: null,
  sources: [],
  legibleAndComplete: null,
  confirmed: false,
};

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full px-3 py-1.5 text-sm transition-colors",
        active
          ? "bg-primary/10 font-medium text-primary"
          : "bg-secondary text-muted-foreground hover:bg-muted",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export function MlrRecordsReviewPanel({
  assessmentId,
  review,
  onSaved,
}: {
  assessmentId: string;
  review: MlrRecordsReview | null;
  onSaved?: () => void;
}) {
  const save = useSaveRecordsReview(assessmentId);

  /*
   * Local until Confirm, the same rule as the mobile card: a half-answered §6
   * written to the server is a statement about the evidence base that nobody
   * has attested.
   *
   * ⚠️ Spread over EMPTY, not `review ?? EMPTY`. The column defaults to `{}`,
   * which is truthy, so the nullish fallback kept it and `sources.map` threw —
   * white-screening the whole report page. The server normalises now; this
   * makes a malformed payload cost a wrong default instead of the page.
   */
  const [draft, setDraft] = useState<MlrRecordsReview>({
    ...EMPTY,
    ...(review ?? {}),
    sources: review?.sources ?? [],
  });

  const confirmed = review?.confirmed === true;
  const dirty = useMemo(
    () =>
      JSON.stringify(draft) !==
      JSON.stringify({
        ...EMPTY,
        ...(review ?? {}),
        sources: review?.sources ?? [],
      }),
    [draft, review],
  );
  const selected = useMemo(
    () => new Set(draft.sources.map((s) => s.key)),
    [draft.sources],
  );

  const setStatus = (status: MlrRecordsStatus) =>
    setDraft((prev) => ({
      ...prev,
      status: prev.status === status ? null : status,
      // Naming records and then saying none arrived is not an answer.
      ...(status === "reviewed"
        ? {}
        : { sources: [], legibleAndComplete: null }),
    }));

  const toggleSource = (key: MlrRecordSourceKey) =>
    setDraft((prev) => {
      const has = prev.sources.some((s) => s.key === key);
      return {
        ...prev,
        status: "reviewed",
        sources: has
          ? prev.sources.filter((s) => s.key !== key)
          : [...prev.sources, { key }],
      };
    });

  const setLegible = (value: boolean) =>
    setDraft((prev) => ({
      ...prev,
      legibleAndComplete: prev.legibleAndComplete === value ? null : value,
    }));

  const onConfirm = async () => {
    try {
      await save.mutateAsync({ ...draft, confirmed: true });
      toast({
        title: "Records review saved",
        description: "§6 will now print what you confirmed.",
      });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Could not save the records review",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="my-3 rounded-md border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <h4 className="flex-1 text-sm font-semibold">
          Which records did you review?
        </h4>
        {confirmed && !dirty ? (
          <Badge variant="secondary" className="gap-1">
            <Check className="h-3 w-3 text-emerald-600" />
            Confirmed
          </Badge>
        ) : (
          <span className="text-xs font-medium text-destructive">
            Not in the report yet
          </span>
        )}
      </div>

      {/* The doctor's own words, above the list read out of them. */}
      {draft.quote ? (
        <p className="mt-2 flex gap-1.5 text-sm italic text-muted-foreground">
          <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{draft.quote}</span>
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {(Object.keys(STATUS_LABEL) as MlrRecordsStatus[]).map((status) => (
          <Chip
            key={status}
            active={draft.status === status}
            onClick={() => setStatus(status)}
          >
            {STATUS_LABEL[status]}
          </Chip>
        ))}
      </div>

      {draft.status === "reviewed" ? (
        <>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Which ones
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {SOURCE_ORDER.map((key) => (
              <Chip
                key={key}
                active={selected.has(key)}
                onClick={() => toggleSource(key)}
              >
                {SOURCE_LABEL[key]}
              </Chip>
            ))}
          </div>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Legible and complete?
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {[true, false].map((value) => (
              <Chip
                key={String(value)}
                active={draft.legibleAndComplete === value}
                onClick={() => setLegible(value)}
              >
                {value ? "Yes" : "No"}
              </Chip>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Leave both unset and the report says nothing about legibility.
          </p>
        </>
      ) : null}

      {confirmed && !dirty ? null : (
        <Button
          type="button"
          size="sm"
          className="mt-4"
          disabled={save.isPending || draft.status == null}
          onClick={() => void onConfirm()}
        >
          {confirmed ? "Save changes" : "Confirm"}
        </Button>
      )}
    </div>
  );
}
